import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  BookOpen,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  EyeOff,
  Loader2,
  Save,
  ShoppingBag,
  Unplug,
} from 'lucide-react';
import { amazonApi, authApi } from '@/services/api';
import { AMAZON_UK_MARKETPLACE_ID } from '@/components/settings/setupGuides';

/**
 * Amazon Marketplaces configure panel — LWA credentials + Seller Central how-to guide.
 */
export default function AmazonIntegrationTab() {
  const qc = useQueryClient();
  const [showSecret, setShowSecret] = useState(false);
  const [showRefresh, setShowRefresh] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [guideOpen, setGuideOpen] = useState(true);
  const [copied, setCopied] = useState<'marketplace' | null>(null);

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile'],
    queryFn: async () => (await authApi.getCompanyProfile()).data.message,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const features: string[] = Array.isArray(profile?.features) ? profile.features : [];
  const packageAllows =
    Boolean(profile?.allow_amazon) ||
    features.includes('allow_amazon') ||
    (Array.isArray(profile?.selected_add_ons) &&
      profile.selected_add_ons.some(
        (a: string | { status?: string; feature_key?: string; addon_code?: string }) => {
          if (typeof a === 'string') return a === 'allow_amazon';
          if (a?.status && a.status !== 'Active') return false;
          return a?.feature_key === 'allow_amazon' || a?.addon_code === 'allow_amazon';
        },
      ));

  const { data: status, refetch } = useQuery({
    queryKey: ['amazonStatus'],
    queryFn: async () => (await amazonApi.getStatus()).data.message,
    enabled: packageAllows,
  });

  const [form, setForm] = useState({
    enable_amazon: false,
    amazon_marketplace_id: AMAZON_UK_MARKETPLACE_ID,
    amazon_seller_id: '',
    amazon_lwa_client_id: '',
    amazon_lwa_client_secret: '',
    amazon_refresh_token: '',
    amazon_sync_products_mode: 'Selected',
    amazon_auto_confirm_orders: false,
    amazon_auto_fetch_orders: false,
  });

  useEffect(() => {
    if (!profile) return;
    setForm((f) => ({
      ...f,
      enable_amazon: Boolean(profile.enable_amazon),
      amazon_marketplace_id: profile.amazon_marketplace_id || AMAZON_UK_MARKETPLACE_ID,
      amazon_seller_id: profile.amazon_seller_id || '',
      amazon_lwa_client_id: profile.amazon_lwa_client_id || '',
      amazon_sync_products_mode: profile.amazon_sync_products_mode || 'Selected',
      amazon_auto_confirm_orders: Boolean(profile.amazon_auto_confirm_orders),
      amazon_auto_fetch_orders: Boolean(profile.amazon_auto_fetch_orders),
    }));
  }, [profile]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { ...form };
      if (!form.amazon_lwa_client_secret) delete payload.amazon_lwa_client_secret;
      if (!form.amazon_refresh_token) delete payload.amazon_refresh_token;
      await authApi.updateCompanyProfile(payload);
    },
    onSuccess: () => {
      toast.success('Amazon settings saved');
      qc.invalidateQueries({ queryKey: ['profile'] });
      void refetch();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || e.message || 'Save failed'),
  });

  const run = async (fn: () => Promise<any>, okText: string) => {
    setMsg(null);
    try {
      const res = await fn();
      const text =
        typeof res === 'string'
          ? res
          : res?.message || okText;
      setMsg({ ok: true, text: typeof text === 'string' ? text : okText });
      void refetch();
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch (e: any) {
      setMsg({ ok: false, text: e?.response?.data?.message || e.message || 'Failed' });
    }
  };

  const copyMarketplace = async () => {
    try {
      await navigator.clipboard.writeText(AMAZON_UK_MARKETPLACE_ID);
      setCopied('marketplace');
      setTimeout(() => setCopied(null), 2000);
    } catch {
      toast.error('Could not copy');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!packageAllows) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900 space-y-3 max-w-xl">
        <p className="font-medium">Amazon add-on required</p>
        <p>Contact Support to purchase the Amazon integration for your company.</p>
        <Link
          to="/support?subject=Enable%20Amazon%20integration"
          className="btn-primary text-sm w-fit inline-flex"
        >
          Contact Support
        </Link>
      </div>
    );
  }

  const connected = Boolean(status?.connected);

  return (
    <div className="space-y-5 max-w-3xl min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
            <ShoppingBag className="w-5 h-5 text-orange-700" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Amazon</h2>
            <p className="text-sm text-gray-500">Seller Central (SP-API) — UK marketplace by default</p>
          </div>
        </div>
        <span
          className={`text-xs font-medium px-2.5 py-1 rounded-full ${
            connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {connected ? 'Connected' : status?.connection_status || 'Disconnected'}
        </span>
      </div>

      {msg && (
        <div
          className={`rounded-lg border px-3 py-2 text-sm flex gap-2 ${
            msg.ok ? 'border-emerald-100 bg-emerald-50 text-emerald-900' : 'border-red-100 bg-red-50 text-red-800'
          }`}
        >
          {msg.ok ? (
            <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      {/* How to get credentials from Amazon */}
      <div className="rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50/80 to-white overflow-hidden min-w-0">
        <button
          type="button"
          onClick={() => setGuideOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left min-w-0"
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-orange-950 min-w-0">
            <BookOpen className="w-4 h-4 shrink-0" />
            <span className="truncate">How to get credentials from Amazon</span>
          </span>
          {guideOpen ? (
            <ChevronUp className="w-4 h-4 text-orange-800 shrink-0" />
          ) : (
            <ChevronDown className="w-4 h-4 text-orange-800 shrink-0" />
          )}
        </button>

        {guideOpen && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700 min-w-0 overflow-x-hidden">
            <p className="text-gray-600 break-words">
              Collect Login with Amazon (LWA) Client ID, Client Secret and Refresh Token from Seller Central —
              then paste them into the form below. Prefer a <strong>private</strong> SP-API app for your own
              seller account.
            </p>

            <div className="rounded-lg border border-orange-200 bg-white px-3 py-2.5 text-xs text-orange-950 min-w-0">
              <p className="font-semibold mb-1">Path to the form</p>
              <p className="font-mono text-[12px] tracking-tight leading-relaxed break-words whitespace-normal">
                Seller Central →{' '}
                <span className="font-bold underline decoration-orange-600/40">Apps and Services</span>
                {' '}→ Develop Apps → Add new app client / Edit app →{' '}
                <span className="font-bold">LWA credentials</span>
              </p>
              <p className="mt-1.5 text-gray-600 break-words">
                Docs:{' '}
                <a
                  href="https://developer-docs.amazon.com/sp-api/docs/viewing-your-application-information-and-credentials"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-800 underline break-all"
                >
                  View LWA credentials
                </a>
                {' · '}
                <a
                  href="https://developer-docs.amazon.com/sp-api/docs/self-authorization"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-800 underline break-all"
                >
                  Self-authorize (Refresh Token)
                </a>
              </p>
            </div>

            <p className="text-sm font-semibold text-gray-900">On Amazon Seller Central / SPP</p>
            <ol className="space-y-3 list-decimal list-inside">
              <li>
                <span className="font-medium text-gray-900">Professional Seller account</span>
                <p className="mt-1 ml-5 text-gray-600">
                  You need a <strong>Professional</strong> selling plan. Sign in as the{' '}
                  <strong>Primary User</strong> of the account you sell on (e.g. Amazon.co.uk).
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Open Develop Apps</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Go to <strong>Apps and Services → Develop Apps</strong> (or Solution Provider Portal → Develop
                  Apps). Complete the developer profile and request SP-API roles (Inventory, Orders, etc.) if
                  prompted.
                </p>
              </li>

              <li>
                <span className="font-medium text-gray-900">Create a private SP-API application</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Choose <strong>Add new app client</strong>. Select Selling Partner API and{' '}
                  <strong>Private</strong> application type.
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">App name:</span>{' '}
                    <code className="font-semibold text-gray-900">SaleDesk POS</code>
                  </p>
                  <p>
                    <span className="text-gray-500">API type:</span>{' '}
                    <span className="font-medium">Selling Partner API</span>
                  </p>
                  <p>
                    <span className="text-gray-500">Application type:</span>{' '}
                    <span className="font-medium">Private</span>
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Copy LWA Client ID &amp; Client Secret</span>
                <p className="mt-1 ml-5 text-gray-600">
                  On the app row, open <strong>View</strong> under <strong>LWA credentials</strong>. Copy:
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-gray-200 bg-gray-50 p-2.5 text-xs space-y-1">
                  <p>
                    <span className="text-gray-500">Client identifier →</span> LWA Client ID
                  </p>
                  <p>
                    <span className="text-gray-500">Client secret →</span> LWA Client Secret
                  </p>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Authorise app → Refresh Token</span>
                <p className="mt-1 ml-5 text-gray-600">
                  Open <strong>Authorize</strong> / <strong>Authorize app</strong> for your Seller Central
                  account (self-authorization). Copy the <strong>Refresh Token</strong> (usually starts with{' '}
                  <code>Atzr|</code>).
                </p>
                <div className="mt-2 ml-5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-950 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>
                    Generating a new refresh token does not always revoke older ones — store only the token you
                    paste into SaleDesk, and never share it in chat.
                  </span>
                </div>
                <div className="mt-2 ml-5 rounded-lg border border-emerald-200 bg-emerald-50/80 p-3 text-xs text-emerald-950 space-y-2">
                  <p className="font-semibold">Paste into SaleDesk (below)</p>
                  <ol className="list-decimal list-inside space-y-1.5 text-gray-800">
                    <li>LWA Client ID → <strong>LWA Client ID</strong></li>
                    <li>LWA Client Secret → <strong>LWA Client Secret</strong></li>
                    <li>Refresh Token → <strong>Refresh Token</strong></li>
                    <li>Save, then click <strong>Test connection</strong></li>
                  </ol>
                </div>
              </li>

              <li>
                <span className="font-medium text-gray-900">Marketplace ID &amp; Seller ID</span>
                <p className="mt-1 ml-5 text-gray-600">
                  For Amazon UK use marketplace ID <code>{AMAZON_UK_MARKETPLACE_ID}</code>. Find Seller ID under
                  Seller Central → Settings → Account Info (Merchant / Seller ID).
                </p>
                <div className="mt-2 ml-5 flex flex-col sm:flex-row gap-2 min-w-0">
                  <code className="min-w-0 flex-1 text-[11px] bg-white border border-orange-100 rounded px-2 py-1.5 break-all">
                    {AMAZON_UK_MARKETPLACE_ID}
                  </code>
                  <button
                    type="button"
                    onClick={() => void copyMarketplace()}
                    className="btn-secondary px-2 py-1.5 text-xs inline-flex items-center justify-center gap-1 shrink-0"
                  >
                    <Copy className="w-3.5 h-3.5" /> Copy UK marketplace ID
                  </button>
                </div>
                {copied === 'marketplace' && (
                  <p className="ml-5 text-xs text-emerald-600 mt-1">Copied</p>
                )}
              </li>
            </ol>

            <div className="rounded-lg bg-white/80 border border-orange-100 p-3 text-xs text-gray-600 space-y-1">
              <p className="font-medium text-gray-800">Tips</p>
              <p>• Keep SaleDesk SKUs identical to Amazon seller SKUs.</p>
              <p>• Start with Selected product sync before All active.</p>
              <p>
                • Official onboarding:{' '}
                <a
                  href="https://developer.amazonservices.com/start/getting-started-for-private-developers"
                  target="_blank"
                  rel="noreferrer"
                  className="text-orange-800 underline"
                >
                  Private developers guide
                </a>
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-5 space-y-4">
        <label className="flex items-center gap-2 text-sm font-medium">
          <input
            type="checkbox"
            checked={form.enable_amazon}
            onChange={(e) => setForm({ ...form, enable_amazon: e.target.checked })}
          />
          Enable Amazon integration
        </label>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-600">Marketplace ID</label>
            <input
              className="input-field mt-1"
              value={form.amazon_marketplace_id}
              onChange={(e) => setForm({ ...form, amazon_marketplace_id: e.target.value })}
              placeholder={AMAZON_UK_MARKETPLACE_ID}
            />
            <p className="text-[11px] text-gray-500 mt-1">UK default: {AMAZON_UK_MARKETPLACE_ID}</p>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Seller ID</label>
            <input
              className="input-field mt-1"
              value={form.amazon_seller_id}
              onChange={(e) => setForm({ ...form, amazon_seller_id: e.target.value })}
              placeholder="From Seller Central → Account Info"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Product sync mode</label>
            <select
              className="input-field mt-1"
              value={form.amazon_sync_products_mode}
              onChange={(e) => setForm({ ...form, amazon_sync_products_mode: e.target.value })}
            >
              <option value="Selected">Selected (Show on Amazon)</option>
              <option value="All active">All active</option>
              <option value="Manual">Manual only</option>
            </select>
          </div>
          <div className="flex flex-col gap-2 justify-end pb-1">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.amazon_auto_fetch_orders}
                onChange={(e) => setForm({ ...form, amazon_auto_fetch_orders: e.target.checked })}
              />
              Auto-fetch orders
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.amazon_auto_confirm_orders}
                onChange={(e) => setForm({ ...form, amazon_auto_confirm_orders: e.target.checked })}
              />
              Auto-confirm mapped orders
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4 space-y-3">
          <p className="text-sm font-medium text-gray-800">LWA credentials</p>
          <p className="text-xs text-gray-500">
            From Seller Central → Develop Apps → LWA credentials and Authorize app. Never share these in chat.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-medium text-gray-600">LWA Client ID</label>
              <input
                className="input-field mt-1"
                value={form.amazon_lwa_client_id}
                onChange={(e) => setForm({ ...form, amazon_lwa_client_id: e.target.value })}
                placeholder="amzn1.application-oa2-client.…"
                autoComplete="off"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                LWA Client Secret {profile?.has_amazon_lwa_secret ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showSecret ? 'text' : 'password'}
                  value={form.amazon_lwa_client_secret}
                  placeholder={profile?.has_amazon_lwa_secret ? '••••••••' : ''}
                  onChange={(e) => setForm({ ...form, amazon_lwa_client_secret: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowSecret((v) => !v)}
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">
                Refresh Token {profile?.has_amazon_refresh_token ? '(saved)' : ''}
              </label>
              <div className="relative mt-1">
                <input
                  className="input-field pr-10"
                  type={showRefresh ? 'text' : 'password'}
                  value={form.amazon_refresh_token}
                  placeholder={profile?.has_amazon_refresh_token ? '••••••••' : 'Atzr|…'}
                  onChange={(e) => setForm({ ...form, amazon_refresh_token: e.target.value })}
                  autoComplete="off"
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                  onClick={() => setShowRefresh((v) => !v)}
                >
                  {showRefresh ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            type="button"
            className="btn-primary text-sm inline-flex items-center gap-1.5"
            disabled={saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {saveMut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
          <button
            type="button"
            className="btn-secondary text-sm"
            onClick={() =>
              void run(async () => {
                const r = await amazonApi.testConnection();
                return r.data.message;
              }, 'Connection OK')
            }
          >
            Test connection
          </button>
          <button
            type="button"
            className="btn-secondary text-sm inline-flex items-center gap-1.5 text-red-700"
            onClick={() =>
              void run(async () => {
                await amazonApi.disconnect();
                setForm((f) => ({
                  ...f,
                  amazon_lwa_client_secret: '',
                  amazon_refresh_token: '',
                }));
                return { message: 'Disconnected' };
              }, 'Disconnected')
            }
          >
            <Unplug className="w-3.5 h-3.5" />
            Disconnect
          </button>
          <Link to="/channel-orders?channel=amazon" className="btn-secondary text-sm w-fit">
            Marketplace Orders
          </Link>
        </div>

        <p className="text-xs text-gray-500">
          Catalogue / order sync jobs will use these LWA credentials. Confirm Test connection succeeds before
          enabling auto-fetch.
        </p>
      </div>
    </div>
  );
}
